/**
 * Which door someone came in by.
 *
 * The platform is served to two hostnames by one process:
 *
 *   people.arikco.com   the firm — Super Admin, Partners, Leads, consultants
 *   client.arikco.com   clients, and nobody else
 *
 * **Why one process and not two deployments.** The session cookie is
 * `SameSite=Strict`, so it is not sent cross-origin at all. A separately
 * hosted interface talking to an API on another origin would simply never
 * send it, and every request would look signed out for a reason that has
 * nothing to do with the code. Each hostname therefore serves its own
 * interface *and* its own `/api` from its own origin, and the cookie is
 * scoped per host by the browser without us arranging anything.
 *
 * **This is not an authorisation boundary and must never be read as one.**
 * The same server, the same database and the same policies sit behind both
 * names. A client who types the firm's hostname is refused by row-level
 * security exactly as before — what this changes is only which surface is
 * drawn, so that the firm's chrome is not offered to a client and a client's
 * portal is not the thing a Lead lands on. Authority stays in Postgres.
 *
 * Detection is at **runtime**, from the hostname, not at build time. There is
 * one build and it is served under both names, so a compile-time flag could
 * not distinguish them.
 */

export type Surface = "firm" | "client" | "either";

/** The first label of each hostname. Everything after it is the firm's zone. */
const FIRM_LABEL = "people";
const CLIENT_LABEL = "client";

/**
 * `either` is the honest answer for localhost, an IP address, a preview
 * deployment, or anything else that is not one of the two production names.
 * In that case the role decides the surface, which is the behaviour the
 * interface had before there were two hostnames — so local development and
 * the test suite are unaffected by any of this.
 */
export function surfaceForHost(
  hostname: string = window.location.hostname,
): Surface {
  const first = hostname.split(".")[0]?.toLowerCase();
  if (first === CLIENT_LABEL) return "client";
  if (first === FIRM_LABEL) return "firm";
  return "either";
}

/**
 * The other door's address, derived by swapping the first label rather than
 * hard-coding `arikco.com`. That keeps staging names, a preview domain and a
 * future rename working without a code change — and it means this file has no
 * opinion about which zone the platform lives in.
 */
export function hostFor(
  surface: "firm" | "client",
  hostname: string = window.location.hostname,
): string {
  const labels = hostname.split(".");
  labels[0] = surface === "firm" ? FIRM_LABEL : CLIENT_LABEL;
  return labels.join(".");
}

/** Full origin for the other door, preserving scheme and any explicit port. */
export function originFor(
  surface: "firm" | "client",
  location: { protocol: string; hostname: string; port: string } = window.location,
): string {
  const port = location.port ? `:${location.port}` : "";
  return `${location.protocol}//${hostFor(surface, location.hostname)}${port}`;
}
